const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "/api/v1";

function buildUrl(token, suffix = "") {
  return `${API_BASE_URL}/application-portal/${encodeURIComponent(
    token,
  )}${suffix}`;
}

async function parseResponse(response) {
  const contentType = response.headers.get("content-type") || "";

  const data = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const message =
      typeof data === "object"
        ? data?.message || data?.error || "Request failed."
        : data || "Request failed.";

    const error = new Error(message);

    error.status = response.status;
    error.data = data;

    throw error;
  }

  return data;
}

async function request(url, options = {}) {
  const headers = {
    Accept: "application/json",
    ...(options.headers || {}),
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  return parseResponse(response);
}

const portalApi = {
  /**
   * Get complete portal application state.
   */
  async get(token) {
    const response = await request(buildUrl(token), {
      method: "GET",
    });

    return response?.data ?? response;
  },

  /**
   * Update a portal section.
   */
  async update(token, section, data) {
    const response = await request(buildUrl(token), {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        section,
        data,
      }),
    });

    return response?.data ?? response;
  },

  /**
   * Backwards-compatible alias.
   */
  async updateSection(token, section, data) {
    return this.update(token, section, data);
  },

  /**
   * Update current portal step.
   */
  async updateCurrentStep(token, currentStep) {
    return this.update(token, "current_step", currentStep);
  },

  /**
   * Upload or replace a client document.
   */
  async uploadDocument(token, document, files, remarks = "") {
    if (!document?.id) {
      throw new Error("Application document ID is required.");
    }

    if (!document?.documentTypeCode) {
      throw new Error("Document type code is required.");
    }

    if (!Array.isArray(files) || !files.length) {
      throw new Error("At least one file is required.");
    }

    const formData = new FormData();

    formData.append("applicationDocumentId", String(document.id));

    formData.append("remarks", remarks || "");

    for (const file of files) {
      formData.append(document.documentTypeCode, file);
    }

    const response = await fetch(buildUrl(token, "/documents"), {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: formData,
    });

    const result = await parseResponse(response);

    return result?.data ?? result;
  },

  async replaceDocumentFile(
    token,
    document,
    documentFileId,
    file,
    remarks = "",
  ) {
    if (!document?.id) {
      throw new Error("Application document ID is required.");
    }

    if (!document?.documentTypeCode) {
      throw new Error("Document type code is required.");
    }

    if (!documentFileId) {
      throw new Error("Document file ID is required.");
    }

    if (!file) {
      throw new Error("A replacement file is required.");
    }

    const formData = new FormData();

    formData.append(document.documentTypeCode, file);

    formData.append("remarks", remarks || "");

    const response = await fetch(
      buildUrl(
        token,
        `/documents/${encodeURIComponent(
          document.id,
        )}/files/${encodeURIComponent(documentFileId)}/replace`,
      ),
      {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: formData,
      },
    );

    const result = await parseResponse(response);

    return result?.data ?? result;
  },

  /**
   * Final client submission.
   */
  async submit(token, { accepted, signature_type, signature_data,location, }) {
    const response = await request(buildUrl(token, "/submit"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        accepted,
        signature_type,
        signature_data,
        location,
      }),
    });

    return response?.data ?? response;
  },
};

export { portalApi };

export default portalApi;
